import { DeleteOutline, DriveFileMoveOutlined } from '@mui/icons-material';
import { Box, Button, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { addDays, isBefore, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getLoadTypeCapabilities, selectRelayLoadTypeCapsById } from '@sophocles/features/reference';
import LightFixtureField from '@sophocles/views/sites/components/LightFixtureField';

const loadCardSx = {
  m: 'var(--space-3)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  bgcolor: 'var(--md-surface-container-lowest)',
  overflow: 'hidden',
};

const loadHeaderSx = {
  px: 'var(--space-3)',
  py: 'var(--space-3)',
  borderBottom: '1px solid var(--border-default)',
  bgcolor: 'transparent',
};

const fieldGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'minmax(0, 1fr) minmax(10rem, 0.35fr)',
  },
  gap: 'var(--space-3)',
  p: 'var(--space-3)',
};

function getFieldName(namePath, field) {
  return namePath ? `${namePath}.${field}` : field;
}

function RelayTextField({ name, label, type = 'text', required = false }) {
  return (
    <Field name={name}>
      {({ field, meta }) => (
        <TextField
          {...field}
          fullWidth
          required={required}
          size="small"
          type={type}
          label={label}
          value={field.value ?? ''}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
        />
      )}
    </Field>
  );
}

function RelayLoadForm({ relay, load, site, namePath = '', availableRelays = [], onDelete = null, onMove = null }) {
  const { t } = useTranslation();
  const [moveAnchorEl, setMoveAnchorEl] = useState(null);

  const loadTypeCapsById = useSelector(selectRelayLoadTypeCapsById);
  const hasFixtures =
    Boolean(load?.hasFixture) ||
    getLoadTypeCapabilities(loadTypeCapsById, relay?.loadType ?? 0).supports_light_fixtures;

  const noNameText = t('general.noName', 'No name');
  const noDescriptionText = t('general.noDescription', 'No description');
  const primaryText = load?.name?.trim() || noNameText;
  const secondaryText = load?.description?.trim() || noDescriptionText;

  const now = site?.time_zone ? toZonedTime(new Date(), site.time_zone) : new Date();

  const determineIfChartingIsActive = end => (end ? isBefore(now, addDays(parseISO(end), 1)) : true);

  const hasActiveContract =
    load?.contract != null &&
    determineIfChartingIsActive(load.contract.end) &&
    load.contract.override != null &&
    determineIfChartingIsActive(load.contract.override);

  const isMoveMenuOpen = Boolean(moveAnchorEl);
  const moveButtonId = `${namePath || 'relay-load'}-move-button`;
  const moveMenuId = `${namePath || 'relay-load'}-move-menu`;

  return (
    <Box sx={loadCardSx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={loadHeaderSx}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color="text.primary">
            {primaryText}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {secondaryText}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
          {onMove && availableRelays.length > 0 && (
            <Fragment>
              <Button
                id={moveButtonId}
                aria-controls={isMoveMenuOpen ? moveMenuId : undefined}
                aria-haspopup="true"
                aria-expanded={isMoveMenuOpen ? 'true' : undefined}
                size="small"
                variant="outlined"
                startIcon={<DriveFileMoveOutlined fontSize="small" />}
                onClick={event => {
                  setMoveAnchorEl(event.currentTarget);
                }}
              >
                {t('actions.move', 'Move')}
              </Button>
              <Menu
                id={moveMenuId}
                anchorEl={moveAnchorEl}
                open={isMoveMenuOpen}
                onClose={() => {
                  setMoveAnchorEl(null);
                }}
                MenuListProps={{
                  'aria-labelledby': moveButtonId,
                }}
              >
                {availableRelays.map(availableRelay => (
                  <MenuItem
                    key={`available-relay-${availableRelay.number}_${availableRelay.id}`}
                    onClick={() => {
                      setMoveAnchorEl(null);
                      onMove(availableRelay.id);
                    }}
                  >
                    {t('sites.relay', 'Relay')} {availableRelay.number}
                  </MenuItem>
                ))}
              </Menu>
            </Fragment>
          )}
          <Tooltip
            title={
              hasActiveContract
                ? t('sites.activeContractDeleteBlocked', 'This load has active contracts and cannot be deleted.')
                : ''
            }
          >
            <span>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={hasActiveContract || !onDelete}
                startIcon={<DeleteOutline fontSize="small" />}
                onClick={onDelete}
              >
                {t('actions.delete', 'Delete')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={fieldGridSx}>
        <RelayTextField name={getFieldName(namePath, 'name')} label={t('general.name', 'Name')} required />
        <RelayTextField name={getFieldName(namePath, 'direction')} label={t('general.direction', 'Direction')} />
        <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
          <RelayTextField
            name={getFieldName(namePath, 'description')}
            label={t('general.description', 'Description')}
          />
        </Box>
        {hasFixtures && (
          <Fragment>
            <LightFixtureField
              required
              fullWidth
              sx={{ width: '100%' }}
              name={getFieldName(namePath, 'lightFixture')}
              label={t('general.lightFixture', 'Light Fixture')}
              size="small"
            />
            <RelayTextField
              name={getFieldName(namePath, 'fixtureQuantity')}
              type="number"
              label={t('general.fixtureQuantity', 'Fixture Quantity')}
              required
            />
          </Fragment>
        )}
      </Box>
    </Box>
  );
}

RelayTextField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  required: PropTypes.bool,
};

RelayLoadForm.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  relay: PropTypes.object.isRequired,
  load: PropTypes.object.isRequired,
  site: PropTypes.object,
  namePath: PropTypes.string,
  availableRelays: PropTypes.array,
  onDelete: PropTypes.func,
  onMove: PropTypes.func,
};

export default RelayLoadForm;
